import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Suspense } from "react";
import { lazyWithRetry } from "./lazyWithRetry";

import { ProtectedRoute } from "./guards/ProtectedRoute";
import { RequireEmployeePortal } from "./guards/RequireEmployeePortal";
import { RequireRole } from "./guards/RequireRole";
import { RequireAdminEmployeeLms } from "./guards/RequireAdminEmployeeLms";

import FamilyBusinessLayout from "../../shared/ui/layouts/family-business/FamilyBusinessLayout.tsx";
import { RequireMentorshipAccess } from "./guards/RequireMentorshipAccess";

/** Full-viewport shell so lazy-route loading matches `body` (avoids gray band on white in light mode). */
const PageFallback = () => (
  <div className="min-h-[100dvh] w-full bg-background dark:bg-surface-base" aria-hidden />
);

// Public pages (route-level code split)
const LandingPage = lazyWithRetry(() => import("../../pages/LandingPage.tsx"));
const ProgramsPage = lazyWithRetry(() => import("../../pages/program/ProgramsPage.tsx"));
const ProgramDetailPage = lazyWithRetry(() => import("../../pages/program/ProgramDetailPage.tsx"));
const CoursesPage = lazyWithRetry(() => import("../../pages/course/CoursesPage.tsx"));
const CourseDetailPage = lazyWithRetry(() => import("../../pages/course/CourseDetailPage.tsx"));
const CourseLearningPage = lazyWithRetry(() => import("../../pages/course/CourseLearningPage.tsx"));
const EmployeeCourseDetailPage = lazyWithRetry(() => import("../../pages/course/EmployeeCourseDetailPage.tsx"));
const EmployeeCourseLearningPage = lazyWithRetry(() => import("../../pages/course/EmployeeCourseLearningPage.tsx"));
const SubscriptionPage = lazyWithRetry(() => import("../../pages/SubscriptionPage.tsx"));
const ArticlesPage = lazyWithRetry(() => import("../../pages/article/ArticlesPage.tsx"));
const ArticleDetailPage = lazyWithRetry(() => import("../../pages/article/ArticleDetailPage.tsx"));
const MentorshipPage = lazyWithRetry(() => import("../../pages/mentorship/MentorshipPage.tsx"));
const MentorshipDetailPage = lazyWithRetry(() => import("../../pages/mentorship/MentorshipDetailPage.tsx"));

// Auth pages
const LoginPage = lazyWithRetry(() => import("../../pages/auth/LoginPage.tsx"));
const RegisterPage = lazyWithRetry(() => import("../../pages/auth/RegisterPage.tsx"));
const VerificationPendingPage = lazyWithRetry(() => import("../../pages/auth/VerificationPendingPage.tsx"));
const VerifyEmailPage = lazyWithRetry(() => import("../../pages/auth/VerifyEmailPage.tsx"));
const ForgotPasswordPage = lazyWithRetry(() => import("../../pages/auth/ForgotPasswordPage.tsx"));
const ResetPasswordPage = lazyWithRetry(() => import("../../pages/auth/ResetPasswordPage.tsx"));
const SsoCallback = lazyWithRetry(() => import("../../pages/auth/SsoCallback.tsx"));

// Profile pages
const ProfilePage = lazyWithRetry(() => import("../../pages/profile/ProfilePage.tsx"));
const MyClassesPage = lazyWithRetry(() => import("../../pages/profile/MyClassesPage.tsx"));
const PaymentHistoryPage = lazyWithRetry(() => import("../../pages/profile/PaymentHistoryPage.tsx"));
const MyCertificatesPage = lazyWithRetry(() => import("../../pages/profile/MyCertificatesPage.tsx"));
const SecurityPage = lazyWithRetry(() => import("../../pages/profile/SecurityPage.tsx"));

// Family business
const FamilyBusinessPage = lazyWithRetry(() => import("../../pages/family-business/FamilyBusinessPage.tsx"));

// Employee portal
const EmployeeCoursesPage = lazyWithRetry(() => import("../../pages/course/EmployeeCoursesPage.tsx"));

// Admin (already split)
const AdminLayout = lazyWithRetry(() => import("../../shared/ui/layouts/admin/AdminLayout"));
const AdminDashboard = lazyWithRetry(() => import("../../pages/admin/AdminDashboard"));
const CourseListPage = lazyWithRetry(() => import("../../pages/admin/course/CourseListPage"));
const CreateCoursePage = lazyWithRetry(() => import("../../pages/admin/course/CreateCoursePage"));
const EditCoursePage = lazyWithRetry(() => import("../../pages/admin/course/EditCoursePage"));
const CreateEmployeeCoursePage = lazyWithRetry(() => import("../../pages/admin/course/CreateEmployeeCoursePage"));
const MentorshipCourseListPage = lazyWithRetry(() => import("../../pages/admin/course/MentorshipCourseListPage"));
const CreateMentorshipCoursePage = lazyWithRetry(() => import("../../pages/admin/course/CreateMentorshipCoursePage"));
const CourseResultsPage = lazyWithRetry(() => import("../../pages/admin/course/CourseResultsPage"));

const ProgramListPage = lazyWithRetry(() => import("../../pages/admin/program/ProgramListPage"));
const CreateProgramPage = lazyWithRetry(() => import("../../pages/admin/program/CreateProgramPage"));
const EditProgramPage = lazyWithRetry(() => import("../../pages/admin/program/EditProgramPage"));

const CreatePromoPage = lazyWithRetry(() => import("../../pages/admin/promo/CreatePromoPage.tsx"));
const PromoListPage = lazyWithRetry(() => import("../../pages/admin/promo/PromoListPage.tsx"));
const EditPromoPage = lazyWithRetry(() => import("../../pages/admin/promo/EditPromoPage.tsx"));

const UserListPage = lazyWithRetry(() => import("../../pages/admin/user/UserListPage.tsx"));
const EditUserPage = lazyWithRetry(() => import("../../pages/admin/user/EditUserPage.tsx"));
const EmployeeReportListPage = lazyWithRetry(() => import("../../pages/admin/employee-report/EmployeeReportListPage.tsx"));
const EmployeeReportDetailPage = lazyWithRetry(() => import("../../pages/admin/employee-report/EmployeeReportDetailPage.tsx"));
const FeedbackReportListPage = lazyWithRetry(() => import("../../pages/admin/feedback-report/FeedbackReportListPage.tsx"));
const FeedbackReportDetailPage = lazyWithRetry(() => import("../../pages/admin/feedback-report/FeedbackReportDetailPage.tsx"));
const MentorshipReportListPage = lazyWithRetry(() => import("../../pages/admin/mentorship-report/MentorshipReportListPage.tsx"));
const MentorshipReportDetailPage = lazyWithRetry(() => import("../../pages/admin/mentorship-report/MentorshipReportDetailPage.tsx"));
const AdminTransactionPage = lazyWithRetry(() => import("../../pages/admin/AdminTransactionPage.tsx"));
const AdminArticleListPage = lazyWithRetry(() => import("../../pages/admin/article/ArticleListPage"));
const CreateArticlePage = lazyWithRetry(() =>
  import("../../pages/admin/article/CreateArticlePage").then((m) => ({ default: m.CreateArticlePage }))
);
const EditArticlePage = lazyWithRetry(() =>
  import("../../pages/admin/article/EditArticlePage").then((m) => ({ default: m.EditArticlePage }))
);
const AdminLandingPage = lazyWithRetry(() => import("../../pages/admin/AdminLandingPage.tsx"));
const AdminTestimonialsPage = lazyWithRetry(() => import("../../pages/admin/testimonial/AdminTestimonialsPage.tsx"));
const PeriodListPage = lazyWithRetry(() => import("../../pages/admin/period/PeriodListPage.tsx"));
const CreatePeriodPage = lazyWithRetry(() => import("../../pages/admin/period/CreatePeriodPage.tsx"));
const EditPeriodPage = lazyWithRetry(() => import("../../pages/admin/period/EditPeriodPage.tsx"));
const PeriodEnrollmentRequestsPage = lazyWithRetry(() => import("../../pages/admin/period/PeriodEnrollmentRequestsPage.tsx"));
const PeriodSksReductionRequestsPage = lazyWithRetry(() => import("../../pages/admin/period/PeriodSksReductionRequestsPage.tsx"));
const JobFamilyListPage = lazyWithRetry(() => import("../../pages/admin/job-family/JobFamilyListPage.tsx"));
const MentorshipPeriodListPage = lazyWithRetry(() => import("../../pages/admin/mentorship-period/MentorshipPeriodListPage.tsx"));
const CreateMentorshipPeriodPage = lazyWithRetry(() => import("../../pages/admin/mentorship-period/CreateMentorshipPeriodPage.tsx"));
const EditMentorshipPeriodPage = lazyWithRetry(() => import("../../pages/admin/mentorship-period/EditMentorshipPeriodPage.tsx"));
const MentorshipPeriodEnrollmentRequestsPage = lazyWithRetry(() => import("../../pages/admin/mentorship-period/MentorshipPeriodEnrollmentRequestsPage.tsx"));

export const AppRouter = () => {
  const location = useLocation();

  return (
    <Suspense fallback={<PageFallback />}>
      <Routes key={location.pathname}>
        {/* Protected Routes */}
        <Route element={<ProtectedRoute />}>
          <Route path="/my-certificates" element={<MyCertificatesPage />} />
          <Route element={<RequireEmployeePortal />}>
            <Route path="/learning/courses" element={<Navigate to="/employee/courses" replace />} />
            <Route path="/employee/courses" element={<EmployeeCoursesPage />} />
            <Route path="/employee/courses/:slug" element={<EmployeeCourseDetailPage />} />
            <Route path="/employee/courses/:slug/learn" element={<EmployeeCourseLearningPage />} />
            <Route path="/employee/courses/:slug/learn/:lessonId" element={<EmployeeCourseLearningPage />} />
          </Route>
        </Route>

        <Route path="/" element={<LandingPage />} />
        <Route path="/family-business" element={<FamilyBusinessLayout />}>
          <Route index element={<FamilyBusinessPage />} />
        </Route>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/sso-callback" element={<SsoCallback />} />
        <Route path="/verification-pending" element={<VerificationPendingPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/programs" element={<ProgramsPage />} />
        <Route path="/courses" element={<CoursesPage />} />
        <Route path="/courses/:slug" element={<CourseDetailPage />} />
        <Route path="/courses/:slug/learn" element={<CourseLearningPage />} />
        <Route path="/courses/:slug/learn/:lessonId" element={<CourseLearningPage />} />
        <Route path="/programs/:slug" element={<ProgramDetailPage />} />
        <Route path="/subscription" element={<SubscriptionPage />} />
        <Route path="/articles" element={<ArticlesPage />} />
        <Route path="/articles/:slug" element={<ArticleDetailPage />} />
        
        <Route element={<RequireMentorshipAccess />}>
            <Route path="/mentorship" element={<MentorshipPage />} />
            <Route path="/mentorship/:slug" element={<MentorshipDetailPage />} />
        </Route>

        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/my-courses" element={<MyClassesPage />} />
        <Route path="/payment-history" element={<PaymentHistoryPage />} />
        <Route path="/certificates" element={<MyCertificatesPage />} />
        <Route path="/security" element={<SecurityPage />} />

        {/* Admin/Instructor Routes */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />

          <Route path="courses" element={<CourseListPage mode="public" />} />
          <Route path="courses/create" element={<CreateCoursePage />} />
          <Route path="courses/:id/edit" element={<EditCoursePage />} />
          <Route path="courses/:courseId/results" element={<CourseResultsPage />} />

          {/* Employee / Internal LMS Courses — admin atau instruktur karyawan */}
          <Route
            path="employee-courses"
            element={
              <RequireAdminEmployeeLms>
                <CourseListPage mode="employee" />
              </RequireAdminEmployeeLms>
            }
          />
          <Route
            path="employee-courses/create"
            element={
              <RequireAdminEmployeeLms>
                <CreateEmployeeCoursePage />
              </RequireAdminEmployeeLms>
            }
          />
          <Route
            path="employee-courses/:id/edit"
            element={
              <RequireAdminEmployeeLms>
                <EditCoursePage />
              </RequireAdminEmployeeLms>
            }
          />
          <Route
            path="employee-courses/:courseId/results"
            element={
              <RequireAdminEmployeeLms>
                <CourseResultsPage />
              </RequireAdminEmployeeLms>
            }
          />

          {/* Mentorship Courses — admin atau instruktur karyawan */}
          <Route
            path="mentorship-courses"
            element={
              <RequireAdminEmployeeLms>
                <MentorshipCourseListPage />
              </RequireAdminEmployeeLms>
            }
          />
          <Route
            path="mentorship-courses/create"
            element={
              <RequireAdminEmployeeLms>
                <CreateMentorshipCoursePage />
              </RequireAdminEmployeeLms>
            }
          />
          <Route
            path="mentorship-courses/:id/edit"
            element={
              <RequireAdminEmployeeLms>
                <EditCoursePage />
              </RequireAdminEmployeeLms>
            }
          />
          <Route
            path="mentorship-courses/:courseId/results"
            element={
              <RequireAdminEmployeeLms>
                <CourseResultsPage />
              </RequireAdminEmployeeLms>
            }
          />

          <Route path="programs" element={<ProgramListPage />} />
          <Route path="programs/create" element={<CreateProgramPage />} />
          <Route path="programs/:id/edit" element={<EditProgramPage />} />

          <Route
            path="promos"
            element={
              <RequireRole allowedRoles={["admin"]}>
                <PromoListPage />
              </RequireRole>
            }
          />
          <Route
            path="promos/create"
            element={
              <RequireRole allowedRoles={["admin"]}>
                <CreatePromoPage />
              </RequireRole>
            }
          />
          <Route
            path="promos/:id/edit"
            element={
              <RequireRole allowedRoles={["admin"]}>
                <EditPromoPage />
              </RequireRole>
            }
          />

          <Route
            path="users"
            element={
              <RequireRole allowedRoles={["admin"]}>
                <UserListPage />
              </RequireRole>
            }
          />
          <Route
            path="users/:id/edit"
            element={
              <RequireRole allowedRoles={["admin"]}>
                <EditUserPage />
              </RequireRole>
            }
          />
          <Route
            path="reports/employees"
            element={
              <RequireRole allowedRoles={["admin"]}>
                <EmployeeReportListPage />
              </RequireRole>
            }
          />
          <Route
            path="reports/employees/:userId"
            element={
              <RequireRole allowedRoles={["admin"]}>
                <EmployeeReportDetailPage />
              </RequireRole>
            }
          />
          <Route
            path="reports/feedback"
            element={
              <RequireRole allowedRoles={["admin"]}>
                <FeedbackReportListPage />
              </RequireRole>
            }
          />
          <Route
            path="reports/feedback/:id"
            element={
              <RequireRole allowedRoles={["admin"]}>
                <FeedbackReportDetailPage />
              </RequireRole>
            }
          />
          <Route
            path="reports/mentorship"
            element={
              <RequireRole allowedRoles={["admin"]}>
                <MentorshipReportListPage />
              </RequireRole>
            }
          />
          <Route
            path="reports/mentorship/:userId"
            element={
              <RequireRole allowedRoles={["admin"]}>
                <MentorshipReportDetailPage />
              </RequireRole>
            }
          />
          <Route path="transactions" element={<AdminTransactionPage />} />

          <Route path="articles" element={<AdminArticleListPage />} />
          <Route path="articles/create" element={<CreateArticlePage />} />
          <Route path="articles/:id/edit" element={<EditArticlePage />} />

          <Route
            path="landing-page"
            element={
              <RequireRole allowedRoles={["admin"]}>
                <AdminLandingPage />
              </RequireRole>
            }
          />

          <Route
            path="testimonials"
            element={
              <RequireRole allowedRoles={["admin"]}>
                <AdminTestimonialsPage />
              </RequireRole>
            }
          />

          <Route
            path="learning-periods"
            element={
              <RequireAdminEmployeeLms>
                <PeriodListPage />
              </RequireAdminEmployeeLms>
            }
          />
          <Route
            path="learning-periods/create"
            element={
              <RequireAdminEmployeeLms>
                <CreatePeriodPage />
              </RequireAdminEmployeeLms>
            }
          />
           <Route
            path="learning-periods/:id/enrollment-requests"
            element={
              <RequireAdminEmployeeLms>
                <PeriodEnrollmentRequestsPage />
              </RequireAdminEmployeeLms>
            }
          />
          <Route
            path="learning-periods/:id/sks-reductions"
            element={
              <RequireAdminEmployeeLms>
                <PeriodSksReductionRequestsPage />
              </RequireAdminEmployeeLms>
            }
          />

          <Route
            path="job-families"
            element={
              <RequireAdminEmployeeLms>
                <JobFamilyListPage />
              </RequireAdminEmployeeLms>
            }
          />
          <Route
            path="mentorship-periods"
            element={
              <RequireAdminEmployeeLms>
                <MentorshipPeriodListPage />
              </RequireAdminEmployeeLms>
            }
          />
          <Route
            path="mentorship-periods/create"
            element={
              <RequireAdminEmployeeLms>
                <CreateMentorshipPeriodPage />
              </RequireAdminEmployeeLms>
            }
          />
          <Route
            path="mentorship-periods/:id/edit"
            element={
              <RequireAdminEmployeeLms>
                <EditMentorshipPeriodPage />
              </RequireAdminEmployeeLms>
            }
          />
          <Route
            path="mentorship-periods/:id/enrollment-requests"
            element={
              <RequireAdminEmployeeLms>
                <MentorshipPeriodEnrollmentRequestsPage />
              </RequireAdminEmployeeLms>
            }
          />
          <Route
            path="learning-periods/:id/edit"
            element={
              <RequireAdminEmployeeLms>
                <EditPeriodPage />
              </RequireAdminEmployeeLms>
            }
          />
        </Route>
      </Routes>
    </Suspense>
  );
};

export default AppRouter;
